import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Table, Button, Modal, Form, InputGroup, Alert } from 'react-bootstrap';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';

const LoanValueManagement = () => {
  const { token, isAdmin, isSuperAdmin } = useAuth();
  const [socios, setSocios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [currentSocio, setCurrentSocio] = useState(null);
  const [formData, setFormData] = useState({ loanValue: 0, loanDescription: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    console.log('[LoanValueManagement] Componente montado. isAdmin:', isAdmin, 'isSuperAdmin:', isSuperAdmin);
    if (isAdmin || isSuperAdmin) {
      console.log('[LoanValueManagement] Llamando a loadSocios()');
      loadSocios();
    } else {
      console.log('[LoanValueManagement] Usuario no es admin ni super-admin');
    }
  }, [isAdmin, isSuperAdmin, token]);

  const loadSocios = async () => {
    console.log('[loadSocios] Iniciando carga de socios...');
    setLoading(true);
    setError(null);
    try {
      // Agregar limit=100 para traer hasta 100 socios sin paginación
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/users?role=socio&limit=100`;
      console.log('[loadSocios] URL:', apiUrl);
      console.log('[loadSocios] Token:', token?.substring(0, 20) + '...');
      
      const response = await axios.get(apiUrl, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('[loadSocios] Respuesta recibida:', response.data);
      setSocios(response.data.users || []);
      console.log('[loadSocios] Socios almacenados en state:', response.data.users?.length || 0);
    } catch (error) {
      console.error('[loadSocios] Error capturado:', error);
      console.error('[loadSocios] Error response:', error.response?.data);
      const errorMsg = 'Error cargando socios: ' + (error.response?.data?.message || error.message);
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (socio) => {
    setCurrentSocio(socio);
    setFormData({ 
      loanValue: socio.loanValue || 0, 
      loanDescription: socio.loanDescription || '' 
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!currentSocio) return;
    
    // Validación
    const loanValueNum = parseFloat(formData.loanValue);
    if (isNaN(loanValueNum) || loanValueNum < 0) {
      toast.error('El valor del préstamo debe ser un número positivo');
      return;
    }

    if (formData.loanDescription.length > 500) {
      toast.error('La descripción no puede exceder 500 caracteres');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/users/${currentSocio._id}`, {
        loanValue: loanValueNum,
        loanDescription: formData.loanDescription
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Préstamo actualizado exitosamente');
      loadSocios();
      setShowModal(false);
    } catch (error) {
      console.error('Error actualizando préstamo:', error);
      toast.error('Error actualizando préstamo: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const filteredSocios = socios.filter(s => 
    s.name?.toLowerCase().includes(search.toLowerCase()) ||
    s.email?.toLowerCase().includes(search.toLowerCase()) ||
    s.socioInfo?.numeroSocio?.toString().includes(search)
  );

  if (!isAdmin && !isSuperAdmin) {
    return <Alert variant="danger">No autorizado para acceder a esta página</Alert>;
  }

  return (
    <Container fluid className="p-4">
      <Row className="mb-4">
        <Col>
          <h2>Gestión de Valores de Préstamo</h2>
          <p className="text-muted">Administra los montos máximos de préstamo para cada socio</p>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}
      
      <Row className="mb-3">
        <Col md={6}>
          <InputGroup>
            <InputGroup.Text><i className="fas fa-search"></i></InputGroup.Text>
            <Form.Control 
              placeholder="Buscar por nombre, email o número de socio..." 
              value={search} 
              onChange={(e) => setSearch(e.target.value)} 
            />
          </InputGroup>
        </Col>
        <Col md={6} className="text-end">
          <Button variant="primary" onClick={loadSocios} disabled={loading}>
            <i className="fas fa-sync-alt"></i> Recargar
          </Button>
        </Col>
      </Row>

      <Row>
        <Col>
          {loading ? (
            <Alert variant="info">Cargando socios...</Alert>
          ) : socios.length === 0 ? (
            <Alert variant="warning">
              <strong>No hay datos</strong><br/>
              Total de socios en el sistema: {socios.length}<br/>
              Si acabas de crear socios, intenta recargando con el botón "Recargar"
            </Alert>
          ) : filteredSocios.length === 0 ? (
            <Alert variant="warning">No hay socios que coincidan con la búsqueda</Alert>
          ) : (
            <Table striped bordered hover responsive>
              <thead className="table-dark">
                <tr>
                  <th>Nombre</th>
                  <th>Email</th>
                  <th>Número Socio</th>
                  <th>Valor Préstamo</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredSocios.map(s => (
                  <tr key={s._id}>
                    <td>{s.name}</td>
                    <td>{s.email}</td>
                    <td>{s.socioInfo?.numeroSocio || '-'}</td>
                    <td className="text-end">${parseFloat(s.loanValue || 0).toFixed(2)}</td>
                    <td>
                      <Button 
                        size="sm" 
                        variant="warning" 
                        onClick={() => handleEdit(s)}
                        disabled={loading}
                      >
                        <i className="fas fa-edit"></i> Editar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Col>
      </Row>

      <Modal show={showModal} onHide={() => !saving && setShowModal(false)}>
        <Modal.Header closeButton disabled={saving}>
          <Modal.Title>Editar Valor de Préstamo - {currentSocio?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Email</Form.Label>
            <Form.Control type="text" value={currentSocio?.email || ''} disabled />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Número de Socio</Form.Label>
            <Form.Control type="text" value={currentSocio?.socioInfo?.numeroSocio || ''} disabled />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Valor Máximo de Préstamo ($)</Form.Label>
            <Form.Control 
              type="number" 
              step="0.01" 
              min="0"
              value={formData.loanValue} 
              onChange={(e) => setFormData({...formData, loanValue: e.target.value})}
              disabled={saving}
            />
          </Form.Group>
          
          <Form.Group>
            <Form.Label>Descripción / Observaciones</Form.Label>
            <Form.Control 
              as="textarea" 
              rows={3} 
              value={formData.loanDescription} 
              onChange={(e) => setFormData({...formData, loanDescription: e.target.value})}
              disabled={saving}
              maxLength={500}
            />
            <small className="text-muted">
              {formData.loanDescription.length}/500 caracteres
            </small>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowModal(false)}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default LoanValueManagement;
